<template>
	<div
		class="teaser-with-number"
		ref="teaserElement"
		:class="{ 'is-web-teaser': !isNativeDevice }"
		@mouseenter="!isNativeDevice ? handleMouseEnter() : null"
		@mouseleave="!isNativeDevice ? handleMouseLeave() : null"
		@focus="isNativeDevice ? showReplica() : null"
		@click="isNativeDevice ? navigateToInnerPage() : null"
		v-focus
	>
		<img
			ref="imageRef"
			@click="play()"
			:src="currentSrc"
			:alt="item.field_image?.meta?.alt"
			:class="{ 'lazy-loaded': isLoaded }"
		/>
	</div>
</template>

<script lang="ts" setup>
	import { computed, ref, onMounted, watch } from "vue"
	import router from "../../router/router"
	import { Movie } from "../../types/Movie"
	import { Series } from "../../types/Series"
	import { isNativeDevice } from "../../helper"
	import { useTeaserPreview } from "../../composables/useTeaserPreview"
	import { useLazyImage } from "../../composables/useLazyImage"
	import { useTeaserStore } from "../../stores/teaser"
	import { useIdle } from "@vueuse/core"
	import { usePlay } from "../../composables/usePlay"
	import { useDeviceManager } from "../../stores/deviceManager"
	import { Capacitor } from "@capacitor/core"
	const { playElement, selectedEpisodeNid } = usePlay()
	const deviceManager = useDeviceManager()

	const props = defineProps<{
		item: Movie | Series
		isFirstVisibleElement?: boolean
		isLastVisibleElement?: boolean
	}>()
	const teaserStore = useTeaserStore()
	const teaserElement = ref<HTMLElement | null>(null)
	const { replicateElement } = useTeaserPreview()
	const { imageRef, currentSrc, isLoaded, observe } = useLazyImage()
	const { idle } = useIdle(500)
	const isHovering = ref(false)

	const contentType = computed(() => props.item.type)
	const imageSrc = computed(() => props.item.field_image?.image_style_uri?.teaser_small || "")

	onMounted(() => {
		if (imageSrc.value) {
			observe(imageSrc.value)
		}
	})

	watch(idle, (isIdle) => {
		if (isIdle && isHovering.value && !teaserStore.state.isPreviewing) {
			triggerPreview()
		}
	})

	const triggerPreview = () => {
		if (props.isFirstVisibleElement) {
			teaserStore.state.isFirstVisibleElement = true
		}
		if (props.isLastVisibleElement) {
			teaserStore.state.isLastVisibleElement = true
		}
		teaserStore.setSelectedItem(props.item, "teaser_with-number")
		teaserStore.setTeaserPosition(teaserElement.value)
		teaserStore.handleMouseOver()
	}

	const handleMouseEnter = () => {
		isHovering.value = true
	}

	const handleMouseLeave = () => {
		isHovering.value = false
	}

	const showReplica = () => {
		teaserStore.setSelectedItem(props.item, "teaser_with-number")
	}

	const navigateToInnerPage = () => {
		router.push({
			name: props.item.type === "node--movie" ? "InnerMovie" : "InnerSeries",
			params: {
				id: props.item.drupal_internal__nid,
			},
		})
	}
	const isMobileDevice = computed(
		() =>
			deviceManager.operatingSystem === "ios" ||
			deviceManager.operatingSystem === "android" ||
			Capacitor.isNativePlatform()
		// () => deviceManager.deviceInfo.platform !== "web" || window.innerWidth <= 480
	)

	const play = async () => {
		if (isMobileDevice.value === true) {
			if (props.item?.type?.includes("movie")) {
				router.push({ name: "InnerMovie", params: { id: props.item.drupal_internal__nid } })
			} else if (props.item.type?.includes("series")) {
				router.push({ name: "InnerSeries", params: { id: props.item.drupal_internal__nid } })
			}
		} else {
			await playElement(props.item)
			await teaserStore.play(
				props.item?.type.includes("movie") ? "movie" : "episode",
				props.item!,
				props.item?.type.includes("movie") ? undefined : selectedEpisodeNid.value,
				router
			)
		}
	}
</script>

<style scoped lang="scss">
	.teaser-with-number {
		&:focus,
		&:hover {
			cursor: pointer;
		}
	}
	img {
		max-width: 263px;
		width: 100%;
		display: block;
		height: auto;
		transition: all 0.3s ease;
		border-radius: 10px;
	}
</style>
